'use client';
import { useTranslation } from '@/app/_shared/hooks/useTranslation';
import '@/app/_shared/i18n/core/client/i18next';
import { IconAskNow } from '@/components/common/Icons/IconAskNow';
import {
	IconDiscover,
	IconDiscoverActive,
} from '@/components/common/Icons/IconDiscover';
import {
	IconForYou,
	IconForYouActive,
} from '@/components/common/Icons/IconForYou';
import {
	IconHistory,
	IconHistoryActive,
} from '@/components/common/Icons/IconHistory';
import {
	IconProfile,
	IconProfileActive,
} from '@/components/common/Icons/IconProfile';
import Logo from '@/components/common/Icons/Logo';
import { AskModalProvider, useOpenAskModal } from '@/contexts/askModal';
import { AuthProvider } from '@/contexts/auth';
import { CommentProvider } from '@/contexts/comment';
import { PopupConfirmProvider } from '@/contexts/popup';
import { ShareProvider } from '@/contexts/share';
import { AskNowModal } from '@/features/conversations/components/AskNowModal';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import Script from 'next/script';
import { type ReactNode, useEffect, useState } from 'react';
import QueryProvider from '../providers/QueryProvider';

// Navigation links configuration
const NAV_LINKS = [
	{
		href: '/for_you',
		icon: IconForYou,
		activeIcon: IconForYouActive,
		label: 'For you',
	},
	{
		href: '/discover',
		icon: IconDiscover,
		activeIcon: IconDiscoverActive,
		label: 'Discover',
	},
	{
		href: '/history',
		icon: IconHistory,
		activeIcon: IconHistoryActive,
		label: 'History',
	},
	{
		href: '/my_profile',
		icon: IconProfile,
		activeIcon: IconProfileActive,
		label: 'Profile',
	},
];

export const AppLayoutContent = ({ children }: { children: ReactNode }) => {
	const pathname = usePathname();
	const [activeNavLinks, setActiveNavLinks] = useState(
		NAV_LINKS.map((link, _index) => ({
			...link,
			isActive: false,
		})),
	);

	const { isOpen, onOpen, onClose } = useOpenAskModal();

	// Update active state based on current URL
	useEffect(() => {
		if (pathname) {
			setActiveNavLinks(
				NAV_LINKS.map((link) => ({
					...link,
					isActive:
						pathname === link.href ||
						// Handle nested routes (e.g., /profile/settings should highlight /profile)
						(pathname.startsWith(link.href) && link.href !== '/'),
				})),
			);
		}
	}, [pathname]);
	const { t } = useTranslation();

	return (
		<>
			<aside className="w-64 bg-white h-full flex flex-col border-r border-gray-200">
				<div className="p-6">
					<div className="flex items-center">
						<Link href="/for_you/curiosity">
							<Logo />
						</Link>
					</div>
				</div>

				<div className="flex flex-col flex-1 justify-center">
					<nav className="px-4 space-y-2">
						{activeNavLinks.map((link) => {
							const Icon = link.isActive ? link.activeIcon : link.icon;
							return (
								<Link
									key={link.href}
									href={link.href}
									className={`flex items-center p-3 rounded-lg hover:bg-[#F5F5F5] ${
										link.isActive ? 'bg-[#F5F5F5]' : ''
									}`}
								>
									<div className="p-2 rounded">
										<Icon />
									</div>
									<span
										className={`ml-3 text-[#101010] ${
											link.isActive ? 'font-bold' : 'font-medium'
										}`}
									>
										{link.label}
									</span>
								</Link>
							);
						})}
					</nav>
				</div>

				<div className="p-4 mt-auto mb-6">
					<button
						type="button"
						className="w-full bg-[#D3F47F] hover:bg-lime-300 text-black py-3 px-4 rounded-lg flex items-center justify-center gap-4 group cursor-pointer"
						onClick={onOpen}
					>
						<div className="group-hover:animate-spin">
							<IconAskNow />
						</div>
						<span className="font-medium">{t('common:askNow')}</span>
					</button>
				</div>
			</aside>
			<main className="flex-1 relative isolate">
				<div className="absolute inset-0 overflow-auto border border-grey-3 border-t-0 border-l-0 ">
					{children}
				</div>
				<AskNowModal isOpen={isOpen} onClose={onClose} />
			</main>
		</>
	);
};

export const AppLayout = ({ children }: { children: ReactNode }) => {
	return (
		<html lang="en">
			<head>
				{/* Google Analytics */}
				<Script
					src="https://www.googletagmanager.com/gtag/js?id=G-1Q32D81LWF"
					strategy="afterInteractive"
				/>
				<Script id="google-analytics" strategy="afterInteractive">
					{`
						window.dataLayer = window.dataLayer || [];
						function gtag(){dataLayer.push(arguments);}
						gtag('js', new Date());
						gtag('config', 'G-1Q32D81LWF');
					`}
				</Script>
			</head>
			<body className="h-screen flex max-w-[980px] mx-auto">
				<QueryProvider>
					<AuthProvider>
						<PopupConfirmProvider>
							<AskModalProvider>
								<AppLayoutContent>
									<CommentProvider>
										<ShareProvider>{children}</ShareProvider>
									</CommentProvider>
								</AppLayoutContent>
							</AskModalProvider>
						</PopupConfirmProvider>
					</AuthProvider>
				</QueryProvider>
			</body>
		</html>
	);
};
